import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context';
import { usePermissions } from '../hooks';

interface UserProfileProps {
  compact?: boolean;
}

export const UserProfile: React.FC<UserProfileProps> = ({ compact = false }) => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { canViewAdminPanel } = usePermissions();
  const location = useLocation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    if (dropdownOpen) {
      document.addEventListener('click', handleClickOutside);
    }
    return () => document.removeEventListener('click', handleClickOutside);
  }, [dropdownOpen]);

  if (!user) return null;

  const getInitials = () => {
    if (user.full_name) {
      const parts = user.full_name.split(' ');
      return parts.length > 1
        ? `${parts[0][0]}${parts[1][0]}`.toUpperCase()
        : parts[0][0].toUpperCase();
    }
    return user.username[0].toUpperCase();
  };

  if (compact) {
    return (
      <div className='relative' ref={dropdownRef}>
        <button
          onClick={() => setDropdownOpen((prev) => !prev)}
          className='w-9 h-9 rounded-full bg-gradient-to-br from-purple-500 to-pink-600 text-white flex items-center justify-center text-sm font-semibold hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-white/30 transition'
          aria-haspopup='menu'
          aria-expanded={dropdownOpen}
          aria-label={t('auth.admin_menu') || 'Menu administrateur'}
          title={t('auth.admin_menu') || 'Menu administrateur'}
        >
          {getInitials()}
        </button>

        {dropdownOpen && (
          <div
            role='menu'
            className='absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-xl py-2 z-50 border border-gray-200'
          >
            <div className='px-4 py-3 border-b border-gray-200'>
              <p className='text-sm font-semibold text-gray-900'>
                {user.full_name || user.username}
              </p>
              <p className='text-xs text-gray-500'>@{user.username}</p>
              <p className='text-xs text-gray-400 mt-1'>{user.email}</p>
              <span className='inline-block px-2 py-1 rounded-full text-xs font-bold uppercase bg-purple-500 text-white mt-2'>
                {t('auth.administrator') || 'Administrateur'}
              </span>
            </div>

            {canViewAdminPanel() && (
              <Link
                to='/admin'
                onClick={() => setDropdownOpen(false)}
                role='menuitem'
                className={`block w-full text-left px-4 py-2 text-sm transition ${
                  location.pathname === '/admin'
                    ? 'text-blue-600 bg-blue-50 font-medium'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                {t('nav.admin') || 'Admin'}
              </Link>
            )}

            <button
              role='menuitem'
              onClick={() => {
                logout();
                setDropdownOpen(false);
              }}
              className='w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 focus:outline-none focus:bg-red-50 transition'
            >
              {t('auth.logout')}
            </button>
          </div>
        )}
      </div>
    );
  }

  // Mode normal - simplifié pour admin
  return (
    <div className='flex items-center gap-4'>
      <div className='text-right'>
        <h3 className='m-0 text-base text-gray-800'>
          {user.full_name || user.username}
        </h3>
        <p className='m-0 text-sm text-gray-600'>@{user.username}</p>
        <p className='m-0 text-xs text-gray-400'>{user.email}</p>
        <span className='inline-block px-2 py-1 rounded-full text-xs font-bold uppercase bg-purple-500 text-white mt-1'>
          {t('auth.administrator') || 'Administrateur'}
        </span>
      </div>
      <button
        onClick={logout}
        className='bg-red-500 text-white border-0 px-4 py-2 rounded-md cursor-pointer text-sm transition-colors hover:bg-red-600'
      >
        {t('auth.logout')}
      </button>
    </div>
  );
};
